import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { UK_SHOE_SIZE_OPTIONS, formatUkShoeLabel } from '../constants/productSizes';
import { AUDIENCE_VALUES, normalizeAudienceLevel } from '../constants/productAudience';
import './ProductManagement.css';

const TYPE_OPTIONS = ['Hard Ball', 'Tape Ball'];
const CATEGORIES_BY_TYPE = {
  'Tape Ball': ['Ball', 'Bat', 'Gloves', 'Shoes'],
  'Hard Ball': ['Bat', 'Ball', 'Shoes', 'Bag', 'Accessories', 'Gloves', 'Pads', 'Helmet', 'Keeping Gloves', 'Keeping Pads']
};

function normalizeUkShoeSizes(raw) {
  if (Array.isArray(raw) && raw.length > 0) {
    return UK_SHOE_SIZE_OPTIONS.filter((s) => raw.includes(s));
  }
  return [...UK_SHOE_SIZE_OPTIONS];
}

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Hard Ball',
    level: 'Both',
    category: CATEGORIES_BY_TYPE['Hard Ball'][0],
    price: '',
    description: '',
    image: '',
    images: [],
    availableUkShoeSizes: [...UK_SHOE_SIZE_OPTIONS]
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUkShoeSize = (code) => {
    setFormData((prev) => {
      const set = new Set(prev.availableUkShoeSizes);
      if (set.has(code)) {
        if (set.size <= 1) return prev;
        set.delete(code);
      } else {
        set.add(code);
      }
      const next = UK_SHOE_SIZE_OPTIONS.filter((s) => set.has(s));
      return { ...prev, availableUkShoeSizes: next };
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'type') {
      setFormData((prev) => {
        const nextCategory = CATEGORIES_BY_TYPE[value][0];
        return {
          ...prev,
          type: value,
          level: prev.level || 'Both',
          category: nextCategory
        };
      });
      return;
    }
    if (name === 'category') {
      setFormData((prev) => {
        const next = { ...prev, category: value };
        if (value === 'Shoes') {
          next.availableUkShoeSizes =
            prev.availableUkShoeSizes?.length > 0 ? prev.availableUkShoeSizes : [...UK_SHOE_SIZE_OPTIONS];
        }
        return next;
      });
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e, imageType = 'image') => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({
          ...prev,
          [imageType]: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleExtraImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), event.target.result].slice(0, 5)
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeExtraImage = (idx) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx)
    }));
  };

  const replaceExtraImage = (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData((prev) => {
        const updated = [...prev.images];
        updated[idx] = event.target.result;
        return { ...prev, images: updated };
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!TYPE_OPTIONS.includes(formData.type)) {
        throw new Error('Invalid type selected');
      }
      if (!AUDIENCE_VALUES.includes(formData.level)) {
        throw new Error('Select who this product is for: Junior, Senior, or Both');
      }
      if (!(CATEGORIES_BY_TYPE[formData.type] || []).includes(formData.category)) {
        throw new Error('Category does not match selected type');
      }

      if (formData.category === 'Shoes') {
        if (!formData.availableUkShoeSizes?.length) {
          throw new Error('Select at least one UK shoe size');
        }
      }

      const productData = {
        name: formData.name,
        sport: 'Cricket',
        type: formData.type,
        level: formData.level,
        category: formData.category,
        price: parseFloat(formData.price),
        stock: 0,
        description: formData.description,
        image: formData.image,
        back_image: null,
        status: 'Active',
        available_uk_shoe_sizes: formData.category === 'Shoes' ? formData.availableUkShoeSizes : null,
        images: formData.images || []
      };

      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);

        if (error) throw error;
        alert('Product updated successfully!');
      } else {
        // Add new product
        const { error } = await supabase
          .from('products')
          .insert([productData]);

        if (error) throw error;
        alert('Product added successfully!');
      }

      // Reset form and refresh
      setFormData({
        name: '',
        type: 'Hard Ball',
        level: 'Both',
        category: CATEGORIES_BY_TYPE['Hard Ball'][0],
        price: '',
        description: '',
        image: '',
        images: [],
        availableUkShoeSizes: [...UK_SHOE_SIZE_OPTIONS]
      });
      setShowAddForm(false);
      setEditingProduct(null);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Failed to save product: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      type: product.type || 'Hard Ball',
      level: normalizeAudienceLevel(product.level),
      category: product.category,
      price: product.price.toString(),
      description: product.description || '',
      image: product.image || '',
      images: Array.isArray(product.images) ? product.images : [],
      availableUkShoeSizes: normalizeUkShoeSizes(product.available_uk_shoe_sizes)
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      alert('Product deleted successfully!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product');
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      type: 'Hard Ball',
      level: 'Both',
      category: CATEGORIES_BY_TYPE['Hard Ball'][0],
      price: '',
      description: '',
      image: '',
      images: [],
      availableUkShoeSizes: [...UK_SHOE_SIZE_OPTIONS]
    });
  };

  return (
    <div className="product-management">
      <div className="pm-header">
        <h2>Product Management</h2>
        {!showAddForm && (
          <button className="add-product-btn" onClick={() => setShowAddForm(true)}>
            + Add New Product
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="product-form-card">
          <h3>{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  {TYPE_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Allocated for *</label>
                <select name="level" value={formData.level} onChange={handleChange} required>
                  <option value="Junior">Junior only</option>
                  <option value="Senior">Senior only</option>
                  <option value="Both">Junior &amp; Senior</option>
                </select>
                <p className="pm-audience-hint">
                  Shown in the shop under Junior / Senior / All filters. &quot;Both&quot; appears only under All.
                </p>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., IPL Mumbai Blue Jersey"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                >
                  {(CATEGORIES_BY_TYPE[formData.type] || []).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Price (£) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  placeholder="45.99"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                placeholder="Product description..."
              />
            </div>

            {formData.category === 'Shoes' && (
              <div className="form-group pm-size-block">
                <label>Available UK shoe sizes *</label>
                <p className="pm-size-help">
                  Tick the UK sizes you stock for this pair. Customers will only see these options at checkout.
                </p>
                <div className="pm-size-grid pm-size-grid--shoes" role="group" aria-label="UK shoe sizes">
                  {UK_SHOE_SIZE_OPTIONS.map((code) => (
                    <label key={code} className="pm-size-chip">
                      <input
                        type="checkbox"
                        checked={formData.availableUkShoeSizes.includes(code)}
                        onChange={() => toggleUkShoeSize(code)}
                      />
                      <span>{formatUkShoeLabel(code)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="form-row image-row">
              <div className="form-group">
                <label>Main Image *</label>
                <div className="image-upload-container">
                  <label htmlFor="front-image-upload" className="upload-button">
                    📷 Upload
                  </label>
                  <input
                    id="front-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'image')}
                    className="file-input-hidden"
                    required={!editingProduct && !formData.image}
                  />
                </div>
                {formData.image ? (
                  <div className="image-preview">
                    <img src={formData.image} alt="Product Preview" />
                    <button 
                      type="button" 
                      className="remove-image-btn"
                      onClick={() => setFormData({...formData, image: ''})}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="image-placeholder">
                    <span>📷</span>
                    <p>No image</p>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Extra Images (up to 5, for slider)</label>
              <div className="image-upload-container">
                <label htmlFor="extra-images-upload" className="upload-button">
                  📷 Add Images
                </label>
                <input
                  id="extra-images-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleExtraImageUpload}
                  className="file-input-hidden"
                />
              </div>
              {formData.images && formData.images.length > 0 && (
                <div className="extra-images-row">
                  {formData.images.map((img, idx) => (
                    <div key={idx} className="extra-image-thumb">
                      <img src={img} alt={`Extra ${idx + 1}`} />
                      <button type="button" className="remove-image-btn" onClick={() => removeExtraImage(idx)}>✕</button>
                      <label className="replace-image-btn" title="Replace image">
                        🔄
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => replaceExtraImage(e, idx)}
                          className="file-input-hidden"
                        />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Saving...' : (editingProduct ? 'Update Product' : 'Add Product')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="products-list">
        {loading && !showAddForm ? (
          <p>Loading products...</p>
        ) : products.length === 0 ? (
          <p>No products yet. Add your first product!</p>
        ) : (
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-card">
                <div className="product-images product-images--single">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="product-thumb-img" />
                  ) : (
                    <div className="no-image">No Image</div>
                  )}
                </div>
                <div className="product-info">
                  <h4>{product.name}</h4>
                  <p className="category">
                    {product.type}
                    {product.level ? ` · ${product.level === 'Both' ? 'Junior & Senior' : product.level}` : ''}
                  </p>
                  <p className="category">{product.category}</p>
                  <p className="price">£{product.price}</p>
                  {product.description && (
                    <p className="description">{product.description}</p>
                  )}
                  {product.category === 'Shoes' &&
                    Array.isArray(product.available_uk_shoe_sizes) &&
                    product.available_uk_shoe_sizes.length > 0 && (
                      <p className="sizes-line">
                        UK: {product.available_uk_shoe_sizes.map(formatUkShoeLabel).join(', ')}
                      </p>
                    )}
                </div>
                <div className="product-actions">
                  <button className="edit-btn" onClick={() => handleEdit(product)}>
                    Edit
                  </button>
                  <button className="delete-btn" onClick={() => handleDelete(product.id)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagement;
